// src/pages/SendPkrPage.js

import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Spin, message, Modal } from "antd";
import {
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  InformationCircleIcon,
  WalletIcon,
  UserIcon,
  LockClosedIcon,
  CameraIcon,
} from "@heroicons/react/24/outline";
import { Html5Qrcode } from "html5-qrcode";

import {
  resolveRecipient as resolveRecipientApi,
  normalizePhone,
} from "../services/baitCashApi";
import "../components/Walletview.css"; // ← ensure .market-back-btn lives here (or in a global css imported here)

const USERS_API = "https://backend.bait-coin.com/api/users";

const SendPkrPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { wallet, baitcashAccount, baitcashBalance } = location.state || {};

  // ==========================================================
  // ALL HOOKS MUST BE CALLED AT TOP LEVEL (before early return)
  // ==========================================================
  const [sendRecipientPhone, setSendRecipientPhone] = useState("");
  const [sendAmount, setSendAmount] = useState("");
  const [sendRecipient, setSendRecipient] = useState(null);
  const [sendResolving, setSendResolving] = useState(false);
  const [sendSending, setSendSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [sendStep, setSendStep] = useState("form");
  const [sendSuccessData, setSendSuccessData] = useState(null);

  // QR scanner state
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState("");
  const scannerRef = useRef(null);
  const qrContainerRef = useRef(null);

  // Ref for phone input
  const phoneInputRef = useRef(null);

  // ------------------------------------------------------------
  // SCANNER CLEANUP – placed before early return
  // ------------------------------------------------------------
  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(console.error);
        scannerRef.current = null;
      }
    };
  }, []);

  // ==========================================================
  // EARLY RETURN (must be AFTER all hooks)
  // ==========================================================
  if (!wallet || !baitcashAccount) {
    navigate("/dashboard");
    return null;
  }

  // ==========================================================
  // NAVIGATION & HELPERS
  // ==========================================================
  const goBack = () => navigate("/dashboard");

  const formatPkr = (value) => {
    if (!Number.isFinite(Number(value))) return "₨ 0.00";
    return `₨ ${Number(value).toLocaleString("en-PK", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // ------------------------------------------------------------
  // QR SCANNER LOGIC
  // ------------------------------------------------------------
  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(console.error);
      scannerRef.current = null;
    }
    setIsScanning(false);
    setScanError("");
  };

  const startScanner = async () => {
    setScanError("");
    setIsScanning(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 300));

      if (!qrContainerRef.current) {
        throw new Error("Scanner container not ready");
      }

      const html5QrCode = new Html5Qrcode("qr-reader-container");
      scannerRef.current = html5QrCode;

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
      };

      await html5QrCode.start(
        { facingMode: "environment" },
        config,
        onScanSuccess,
        onScanError
      );
    } catch (err) {
      console.error("Scanner start error:", err);
      setScanError("Unable to access camera. Please allow camera permissions.");
      setIsScanning(false);
    }
  };

  const onScanSuccess = (decodedText) => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(console.error);
      scannerRef.current = null;
    }

    let phoneNumber = null;
    if (decodedText.startsWith("BAITCASH:")) {
      phoneNumber = decodedText.replace("BAITCASH:", "").trim();
    } else {
      const normalized = normalizePhone(decodedText);
      if (normalized) {
        phoneNumber = normalized;
      } else {
        setScanError("Invalid QR code. Please scan a BAIT Cash QR code.");
        setIsScanning(false);
        return;
      }
    }

    const validPhone = normalizePhone(phoneNumber);
    if (!validPhone) {
      setScanError("Invalid phone number in QR code.");
      setIsScanning(false);
      return;
    }

    setIsScanning(false);
    setSendRecipientPhone(validPhone);
    setSendRecipient(null);
    setSendError("");

    setTimeout(() => {
      resolveRecipient(validPhone);
    }, 300);

    message.success("QR scanned successfully!");
  };

  const onScanError = (error) => {
    if (error && !error.includes("No QR")) {
      console.warn("Scan error:", error);
    }
  };

  // ------------------------------------------------------------
  // RECIPIENT RESOLVE
  // ------------------------------------------------------------
  const resolveRecipient = async (phoneOverride = null) => {
    if (sendResolving) return;
    const phone = phoneOverride || normalizePhone(sendRecipientPhone);
    if (!phone) {
      setSendError("Please enter a valid phone number.");
      return;
    }

    if (!baitcashAccount?.phoneNumber) {
      setSendError("Your BAIT Cash account could not be identified.");
      return;
    }

    const senderNormalized = normalizePhone(baitcashAccount.phoneNumber);
    if (phone === senderNormalized) {
      setSendError("You cannot transfer BAIT Cash to yourself.");
      return;
    }

    setSendResolving(true);
    setSendError("");
    setSendRecipient(null);

    try {
      const data = await resolveRecipientApi(phone);
      if (data?.recipient) {
        setSendRecipient(data.recipient);
        setSendError("");
        message.success(`Recipient found: ${data.recipient.fullName}`);
      } else {
        throw new Error("Recipient BAIT Cash account was not found.");
      }
    } catch (err) {
      setSendRecipient(null);
      setSendError(err?.message || "Unable to find this BAIT Cash account.");
    } finally {
      setSendResolving(false);
    }
  };

  const handlePhoneBlur = async () => {
    if (!sendRecipientPhone.trim() || sendRecipient) return;
    await resolveRecipient();
  };

  const handlePhoneKeyDown = async (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      phoneInputRef.current?.blur();
      await resolveRecipient();
    }
  };

  // ------------------------------------------------------------
  // SEND LOGIC
  // ------------------------------------------------------------
  const validateSendAmount = () => {
    if (!sendAmount || sendAmount.trim() === "")
      return "Please enter an amount.";
    if (!/^\d+(\.\d{1,2})?$/.test(sendAmount.trim()))
      return "Maximum 2 decimal places are allowed.";
    const value = Number(sendAmount);
    if (!Number.isFinite(value)) return "Please enter a valid amount.";
    if (value <= 0) return "Amount must be greater than zero.";
    if (value > Number(baitcashBalance)) return "Insufficient BAIT Cash balance.";
    return "";
  };

  const handleReviewSend = () => {
    setSendError("");
    if (!baitcashAccount?.phoneNumber) {
      setSendError("Your BAIT Cash account could not be identified.");
      return;
    }
    if (!sendRecipient) {
      setSendError("Please enter a valid recipient phone number.");
      return;
    }
    const amountError = validateSendAmount();
    if (amountError) {
      setSendError(amountError);
      return;
    }
    setSendStep("confirm");
  };

  const handleExecuteSend = async () => {
    setSendError("");
    if (!baitcashAccount?.phoneNumber) {
      setSendError("Your BAIT Cash account could not be identified.");
      return;
    }
    if (!sendRecipient) {
      setSendError("Recipient information is missing.");
      return;
    }
    const amountError = validateSendAmount();
    if (amountError) {
      setSendError(amountError);
      setSendStep("form");
      return;
    }

    setSendSending(true);
    try {
      const senderPhone = baitcashAccount.phoneNumber;
      const recipientPhone = sendRecipient.phoneNumber;
      if (senderPhone === recipientPhone) {
        throw new Error("You cannot send BAIT Cash to yourself.");
      }

      const idempotencyKey = `BAIT-WALLET-${Date.now()}-${Math.random()
        .toString(36)
        .substring(2, 8)
        .toUpperCase()}`;

      const response = await fetch(`${USERS_API}/transfer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          senderPhone,
          recipientPhone,
          amountPKR: sendAmount,
          idempotencyKey,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(
          data?.error || data?.message || "BAIT Cash transfer failed."
        );
      }

      setSendSuccessData(data);
      setSendStep("success");
      message.success("Transfer successful!");
    } catch (err) {
      setSendError(err?.message || "BAIT Cash transfer failed.");
    } finally {
      setSendSending(false);
    }
  };

  const resetAndGoBack = () => {
    setSendRecipientPhone("");
    setSendAmount("");
    setSendRecipient(null);
    setSendError("");
    setSendStep("form");
    setSendSuccessData(null);
    setSendSending(false);
    setSendResolving(false);
    goBack();
  };

  // ==========================================================
  // STYLES
  // ==========================================================
  const styles = {
    page: {
      width: "100%",
      minHeight: "80vh",
      background: "#faf8f5",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      overflowX: "hidden",
    },

    container: {
      width: "calc(100% - 40px)",
      maxWidth: 540,
      margin: "0 auto 32px",
      padding: "0 0 16px",
      boxSizing: "border-box",
    },

    pageTitle: {
      width: "100%",
      marginBottom: 24,
      boxSizing: "border-box",
    },
    titleWrapper: {
      position: "relative",
      width: "100%",
      minHeight: 52,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
    },
    // 🔴 REMOVED: backButton inline style — now handled by .market-back-btn CSS
    titleContent: {
      width: "100%",
      textAlign: "center",
      padding: "0 48px",
      boxSizing: "border-box",
    },

    // =====================================================
    // TITLE — matches BAIT PAY brand styling
    // =====================================================
    title: {
      margin: "0 0 5px",

      fontFamily:
        "'Inter', 'Poppins', 'Segoe UI', sans-serif",

      fontSize: 26,

      lineHeight: 1.1,

      fontWeight: 600,

      letterSpacing: "1.4px",

      color: "#8a5a1f",

      WebkitTextStroke:
        "0.4px #6b3d0a",

      textShadow:
        "0 1px 0 rgba(255, 240, 200, 0.6), 0 2px 6px rgba(154, 106, 24, 0.22)",
    },

    // =====================================================
    // SUBTITLE — muted bronze
    // =====================================================
    subtitle: {
      margin: "4px 0 0",

      fontFamily:
        "'Poppins', 'Segoe UI', sans-serif",

      fontSize: 11,

      fontWeight: 500,

      letterSpacing: "0.6px",

      lineHeight: 1.3,

      color: "#a07a3f",

      opacity: 0.95,
    },

    formContainer: {
      width: "100%",
      boxSizing: "border-box",
    },
    confirmContainer: {
      width: "100%",
      boxSizing: "border-box",
    },
    successContainer: {
      width: "100%",
      boxSizing: "border-box",
      textAlign: "center",
    },

    sendErrorBox: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      background: "#fef3e8",
      color: "#9a5b12",
      border: "1px solid #f5d9b8",
      borderRadius: "10px",
      padding: "8px 12px",
      marginBottom: "16px",
      fontSize: "12px",
      fontWeight: 500,
    },

    sendBalanceCard: {
      background: "#eee3d4",
      borderRadius: "16px",
      padding: "20px 24px",
      marginBottom: "24px",
      border: "1px solid #f0ba5d",
      boxShadow: "0 4px 16px rgba(0,0,0,0.03)",
    },
    sendBalanceLabel: {
      fontSize: "12px",
      fontWeight: 600,
      color: "#92703d",
      letterSpacing: "0.3px",
      textTransform: "uppercase",
      marginBottom: "4px",
    },
    sendBalanceAmount: {
      fontSize: "32px",
      fontWeight: 800,
      color: "#2a1f12",
      margin: "2px 0 6px",
      letterSpacing: "-0.5px",
    },
    sendBalanceUser: {
      fontSize: "13px",
      color: "#8a7a62",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },

    sendFormSection: {
      marginBottom: "20px",
    },
    sendFormLabel: {
      display: "block",
      fontSize: "13px",
      fontWeight: 600,
      color: "#5a4a32",
      marginBottom: "6px",
    },

    sendInputRow: {
      display: "flex",
      gap: "8px",
      alignItems: "center",
    },
    sendPhoneInput: {
      flex: 1,
      height: "48px",
      padding: "0 14px",
      borderRadius: "10px",
      border: "1px solid #e0d6c8",
      fontSize: "15px",
      fontWeight: 500,
      color: "#2a1f12",
      background: "#ffffff",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
      outline: "none",
    },
    scanButton: {
      height: "48px",
      minWidth: "48px",
      padding: "0 12px",
      borderRadius: "10px",
      border: "1px solid #d5cbbc",
      background: "#ffffff",
      color: "#9a6a18",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    sendResolveButton: {
      height: "48px",
      padding: "0 16px",
      borderRadius: "10px",
      border: "none",
      background: "linear-gradient(135deg, #9a6a18, #c18a32)",
      color: "#ffffff",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
      whiteSpace: "nowrap",
      transition: "all 0.2s ease",
      boxShadow: "0 2px 8px rgba(154,106,24,0.12)",
    },

    sendFormHelper: {
      marginTop: "4px",
      fontSize: "11px",
      color: "#9b8b73",
      lineHeight: 1.4,
    },

    sendAmountInput: {
      width: "100%",
      height: "56px",
      padding: "0 16px",
      borderRadius: "10px",
      border: "1px solid #e0d6c8",
      fontSize: "26px",
      fontWeight: 700,
      color: "#2a1f12",
      background: "#ffffff",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
      outline: "none",
    },

    sendReviewButton: {
      width: "100%",
      height: "52px",
      borderRadius: "12px",
      border: "none",
      background: "linear-gradient(135deg, #9a6a18, #c18a32)",
      color: "#ffffff",
      fontSize: "15px",
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      marginTop: "8px",
      boxShadow: "0 4px 16px rgba(154,106,24,0.18)",
    },

    securityNote: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      marginTop: "24px",
      padding: "12px 0",
      borderTop: "1px solid #ece4d6",
      fontSize: "12px",
      color: "#9b8b73",
    },
    securityIcon: {
      color: "#9a6a18",
    },

    sendConfirmCard: {
      background: "#ffffff",
      borderRadius: "16px",
      padding: "24px 20px",
      textAlign: "center",
      border: "1px solid #ece4d6",
      boxShadow: "0 4px 16px rgba(0,0,0,0.03)",
      marginBottom: "20px",
    },
    sendConfirmIcon: {
      width: "64px",
      height: "64px",
      borderRadius: "50%",
      background: "linear-gradient(135deg, #f4ead9, #e8dcc8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#9a6a18",
      margin: "0 auto 14px",
    },
    sendConfirmLabel: {
      fontSize: "12px",
      color: "#8a7a62",
      marginBottom: "2px",
    },
    sendConfirmName: {
      fontSize: "18px",
      fontWeight: 700,
      margin: "2px 0",
      color: "#2a1f12",
    },
    sendConfirmPhone: {
      fontSize: "14px",
      color: "#8a7a62",
      marginBottom: "14px",
    },
    sendConfirmAmount: {
      fontSize: "30px",
      fontWeight: 800,
      color: "#2a1f12",
      padding: "10px 0",
      borderTop: "1px solid #ece4d6",
      borderBottom: "1px solid #ece4d6",
    },
    sendRemainingBalance: {
      fontSize: "13px",
      color: "#8a7a62",
      marginTop: "14px",
    },
    sendButtonRow: {
      display: "flex",
      gap: "10px",
      marginTop: "4px",
    },
    sendCancelButton: {
      flex: 1,
      height: "46px",
      borderRadius: "10px",
      border: "1px solid #d5cbbc",
      background: "#ffffff",
      color: "#5a4a32",
      fontSize: "14px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    sendConfirmButton: {
      flex: 2,
      height: "46px",
      borderRadius: "10px",
      border: "none",
      background: "linear-gradient(135deg, #9a6a18, #c18a32)",
      color: "#ffffff",
      fontSize: "14px",
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 4px 12px rgba(154,106,24,0.16)",
    },

    sendSuccessHeader: {
      textAlign: "center",
      padding: "16px 0 8px",
    },
    sendSuccessAmount: {
      textAlign: "center",
      fontSize: "34px",
      fontWeight: 800,
      color: "#2a1f12",
      padding: "10px 0",
    },
    sendSuccessCard: {
      background: "#f8f4ec",
      borderRadius: "12px",
      padding: "14px 16px",
      margin: "10px 0",
      border: "1px solid #ece4d6",
    },
    sendSuccessRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "6px 0",
      fontSize: "13px",
      color: "#5a4a32",
      borderBottom: "1px solid #ece4d6",
    },
    sendDoneButton: {
      width: "100%",
      marginTop: "16px",
      height: "48px",
      borderRadius: "10px",
      border: "none",
      background: "linear-gradient(135deg, #9a6a18, #c18a32)",
      color: "#ffffff",
      fontSize: "14px",
      fontWeight: 700,
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 4px 12px rgba(154,106,24,0.16)",
    },
  };

  // ==========================================================
  // RENDER
  // ==========================================================
  return (
    <div style={styles.page}>
      {/* Main content */}
      <div style={styles.container}>
        {/* Page Title */}
        <div style={styles.pageTitle}>
          <div style={styles.titleWrapper}>
            {/* BACK BUTTON — uses shared .market-back-btn styles */}
            <button
              type="button"
              className="market-back-btn"
              onClick={goBack}
              title="Back to Dashboard"
              aria-label="Back to Dashboard"
            >
              <ArrowLeftIcon width={18} height={18} />
            </button>

            <div style={styles.titleContent}>
              <h1 style={styles.title}>Send PKR</h1>
              <p style={styles.subtitle}>
                Transfer PKR to another BAITPay user
              </p>
            </div>
          </div>
        </div>

        {/* Step-based content */}
        {sendStep === "success" && sendSuccessData ? (
          // SUCCESS screen
          <div style={styles.successContainer}>
            <div style={styles.sendSuccessHeader}>
              <CheckCircleIcon
                width={60}
                height={60}
                style={{ color: "#4cae5b" }}
              />
              <h2
                style={{
                  margin: "14px 0 2px",
                  fontSize: "22px",
                  fontWeight: 700,
                }}
              >
                Transfer Successful
              </h2>
              <p style={{ fontSize: "13px", color: "#8a7a62" }}>
                BAIT Cash Sent Successfully
              </p>
            </div>
            <div style={styles.sendSuccessAmount}>
              {formatPkr(sendSuccessData.transfer?.amountPKR || sendAmount)}
            </div>
            <div style={styles.sendSuccessCard}>
              <div style={styles.sendSuccessRow}>
                <span>Recipient</span>
                <strong>{sendRecipient?.fullName}</strong>
              </div>
              <div style={styles.sendSuccessRow}>
                <span>Phone</span>
                <strong>{sendRecipient?.phoneNumber}</strong>
              </div>
              <div style={styles.sendSuccessRow}>
                <span>Reference</span>
                <strong style={{ fontSize: "11px", wordBreak: "break-all" }}>
                  {sendSuccessData.transfer?.reference || "N/A"}
                </strong>
              </div>
              <div style={styles.sendSuccessRow}>
                <span>New Balance</span>
                <strong>{formatPkr(baitcashBalance)}</strong>
              </div>
            </div>
            <button onClick={resetAndGoBack} style={styles.sendDoneButton}>
              Done
            </button>
          </div>
        ) : sendStep === "confirm" ? (
          // CONFIRM screen
          <div style={styles.confirmContainer}>
            <div style={styles.sendConfirmCard}>
              <div style={styles.sendConfirmIcon}>
                <WalletIcon width={44} height={44} />
              </div>
              <p style={styles.sendConfirmLabel}>Sending to</p>
              <h3 style={styles.sendConfirmName}>
                {sendRecipient?.fullName}
              </h3>
              <p style={styles.sendConfirmPhone}>
                {sendRecipient?.phoneNumber}
              </p>
              <div style={styles.sendConfirmAmount}>
                {formatPkr(sendAmount)}{" "}
                <span
                  style={{
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "#8a7a62",
                  }}
                >
                  PKR
                </span>
              </div>
              <div style={styles.sendRemainingBalance}>
                Remaining balance{" "}
                <strong>
                  {formatPkr(baitcashBalance - Number(sendAmount))}
                </strong>
              </div>
            </div>
            {sendError && (
              <div style={styles.sendErrorBox}>
                <InformationCircleIcon width={18} height={18} />
                <span>{sendError}</span>
              </div>
            )}
            <div style={styles.sendButtonRow}>
              <button
                onClick={() => setSendStep("form")}
                disabled={sendSending}
                style={styles.sendCancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleExecuteSend}
                disabled={sendSending}
                style={styles.sendConfirmButton}
              >
                {sendSending ? (
                  <>
                    <Spin size="small" style={{ marginRight: 6 }} /> Sending...
                  </>
                ) : (
                  <>
                    Send <ArrowRightIcon width={16} height={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          // FORM screen (with scanner)
          <div style={styles.formContainer}>
            {sendError && (
              <div style={styles.sendErrorBox}>
                <InformationCircleIcon width={18} height={18} />
                <span>{sendError}</span>
              </div>
            )}

            <div style={styles.sendBalanceCard}>
              <div style={styles.sendBalanceLabel}>Available BAIT Cash</div>
              <div style={styles.sendBalanceAmount}>
                {formatPkr(baitcashBalance)}
              </div>
              {baitcashAccount?.phoneNumber && (
                <div style={styles.sendBalanceUser}>
                  <UserIcon width={14} height={14} />
                  {baitcashAccount.fullName} • {baitcashAccount.phoneNumber}
                </div>
              )}
            </div>

            <div style={styles.sendFormSection}>
              <label style={styles.sendFormLabel}>
                Recipient Phone Number
              </label>
              <div style={styles.sendInputRow}>
                <input
                  type="tel"
                  ref={phoneInputRef}
                  value={sendRecipientPhone}
                  onChange={(e) => {
                    setSendRecipientPhone(e.target.value);
                    setSendRecipient(null);
                    setSendError("");
                  }}
                  onBlur={handlePhoneBlur}
                  onKeyDown={handlePhoneKeyDown}
                  placeholder="03XXXXXXXXX or +923XXXXXXXXX"
                  disabled={sendResolving || sendSending}
                  style={styles.sendPhoneInput}
                />
                <button
                  onClick={startScanner}
                  disabled={sendResolving || sendSending}
                  style={styles.scanButton}
                  title="Scan QR code"
                  aria-label="Scan QR code"
                >
                  <CameraIcon width={20} height={20} />
                </button>
                <button
                  onClick={() => resolveRecipient()}
                  disabled={
                    sendResolving || !sendRecipientPhone.trim() || sendSending
                  }
                  style={styles.sendResolveButton}
                >
                  {sendResolving ? (
                    <>
                      <Spin size="small" style={{ marginRight: 4 }} /> Finding
                    </>
                  ) : (
                    "Find"
                  )}
                </button>
              </div>
              <div style={styles.sendFormHelper}>
                Enter 11-digit number (e.g., 03336931777) or with +92 prefix
                (e.g., +923336931777)
              </div>
              {sendRecipient && (
                <div
                  style={{
                    marginTop: "6px",
                    fontSize: "13px",
                    color: "#4cae5b",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <CheckCircleIcon width={16} height={16} />
                  Recipient found: {sendRecipient.fullName}
                </div>
              )}
            </div>

            <div style={styles.sendFormSection}>
              <label style={styles.sendFormLabel}>Amount</label>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={sendAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^\d*(\.\d{0,2})?$/.test(value)) {
                      setSendAmount(value);
                      setSendError("");
                    }
                  }}
                  placeholder="0.00"
                  disabled={sendSending}
                  style={styles.sendAmountInput}
                />
                <span
                  style={{
                    position: "absolute",
                    right: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#8a7a62",
                  }}
                >
                  PKR
                </span>
              </div>
              <div style={styles.sendFormHelper}>
                Enter amount to send in PKR
              </div>
            </div>

            <button
              onClick={handleReviewSend}
              disabled={
                !baitcashAccount?.phoneNumber ||
                !sendRecipient ||
                !sendAmount ||
                sendSending
              }
              style={styles.sendReviewButton}
            >
              Review Transfer
              <ArrowRightIcon width={18} height={18} />
            </button>

            <div style={styles.securityNote}>
              <LockClosedIcon
                width={14}
                height={14}
                style={styles.securityIcon}
              />
              <span>Secure • Instant • 24/7</span>
              <span style={{ marginLeft: "auto", opacity: 0.7 }}>
                Your transfers are protected with bank-level security
              </span>
            </div>
          </div>
        )}
      </div>

      {/* QR Scanner Modal */}
      <Modal
        title={null}
        open={isScanning}
        onCancel={stopScanner}
        footer={null}
        width={400}
        centered
        closable={true}
        maskClosable={true}
        destroyOnClose={true}
        styles={{
          body: { padding: 0, borderRadius: "16px" },
          content: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          },
          mask: { backdropFilter: "blur(4px)" },
        }}
      >
        <div style={{ padding: "20px", textAlign: "center" }}>
          <h3
            style={{
              margin: "0 0 12px",
              fontSize: "18px",
              fontWeight: 700,
              color: "#2a1f12",
            }}
          >
            Scan QR Code
          </h3>
          {scanError && (
            <div
              style={{
                color: "#dc2626",
                fontSize: "13px",
                marginBottom: "12px",
              }}
            >
              {scanError}
            </div>
          )}
          <div
            id="qr-reader-container"
            ref={qrContainerRef}
            style={{
              width: "100%",
              maxWidth: "320px",
              margin: "0 auto",
              background: "#000",
              borderRadius: "12px",
              overflow: "hidden",
            }}
          />
          <button
            onClick={stopScanner}
            style={{
              marginTop: "16px",
              padding: "10px 24px",
              borderRadius: "10px",
              border: "1px solid #d5cbbc",
              background: "#ffffff",
              color: "#5a4a32",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
          >
            Cancel
          </button>
          <div
            style={{
              fontSize: "12px",
              color: "#9b8b73",
              marginTop: "8px",
            }}
          >
            Point your camera at the BAIT Cash QR code
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SendPkrPage;